import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ArrowLeft, X, Check, Plus, Calendar, Send } from 'lucide-react';
import { getTimeCapsule, getStewardship, getMilestonesForPerson, getMilestoneTemplatesByCategory, createMilestoneFromTemplate, axiosInstance } from '../../services/api';
import { createWithMedia, updateWithMedia, partitionMedia } from '../../services/mediaService';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HubLayout from '../common/HubLayout';
import SharingScope from '../common/SharingScope';
import MediaUploader from './MediaUploader';
import StewardSelection from './StewardSelection';
import RecipientSelector from './RecipientSelector';
import { useFamily } from '../../contexts/FamilyContext';
import { getUser, getFamilies } from '../../utils/secureStorage';

// Color constants
const colors = {
  primaryGreen: '#556B2F',
  secondaryGreen: '#6B8E23',
  accentGreen: '#4A5D23',
  highlightOrange: '#E65100',
  backgroundCream: '#F5F5DC',
  chatBg: '#FAFAF5',
  systemBubble: '#FFFFFF',
  userBubble: '#556B2F',
  textDark: '#333333',
  textMedium: '#666666',
  textMuted: '#999999',
};

// Toolbar options for Quill editor
const TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline'],
  [{ color: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean']
];

const FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'color'];

// Milestone icon and color mapping
const getMilestoneStyle = (milestoneName) => {
  const name = (milestoneName || '').toLowerCase();
  if (name.includes('birthday') || name.includes('18')) return { icon: '🎂', bg: '#FFF8E1', border: '#FFB74D' };
  if (name.includes('graduation') || name.includes('graduate')) return { icon: '🎓', bg: '#E8F5E9', border: '#81C784' };
  if (name.includes('wedding') || name.includes('marriage')) return { icon: '💒', bg: '#FCE4EC', border: '#F48FB1' };
  if (name.includes('baby') || name.includes('child') || name.includes('parent')) return { icon: '👶', bg: '#E3F2FD', border: '#64B5F6' };
  if (name.includes('job') || name.includes('career') || name.includes('first job')) return { icon: '💼', bg: '#FFF3E0', border: '#FFB74D' };
  if (name.includes('home') || name.includes('house')) return { icon: '🏠', bg: '#E8F5E9', border: '#81C784' };
  if (name.includes('retire')) return { icon: '🌴', bg: '#FFF8E1', border: '#FFB74D' };
  return { icon: '🎯', bg: '#F5F5F0', border: '#E0E0E0' };
};

// System Message Component
const SystemMessage = ({ children }) => (
  <div className="flex items-start gap-3 mb-4">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: colors.secondaryGreen }}
    >
      <span className="text-white text-lg">✉️</span>
    </div>
    <div
      className="rounded-2xl px-5 py-4 shadow-sm max-w-lg"
      style={{ backgroundColor: colors.systemBubble }}
    >
      {children}
    </div>
  </div>
);

// User Message Component
const UserMessage = ({ children }) => (
  <div className="flex justify-end mb-4">
    <div
      className="rounded-2xl px-5 py-3 shadow-sm max-w-md"
      style={{ backgroundColor: colors.userBubble }}
    >
      <span className="text-white text-sm">{children}</span>
    </div>
  </div>
);

// Milestone Card Component
const MilestoneCard = ({ milestone, selected, onClick }) => {
  const style = getMilestoneStyle(milestone.name);
  const formattedDate = milestone.target_date
    ? new Date(milestone.target_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : 'Date TBD';

  return (
    <div
      className={`relative rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
        selected ? 'ring-2 ring-offset-2' : ''
      }`}
      style={{
        backgroundColor: style.bg,
        borderWidth: selected ? 2 : 1,
        borderStyle: 'solid',
        borderColor: style.border,
        minWidth: '140px'
      }}
      onClick={onClick}
    >
      {selected && (
        <div
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: colors.secondaryGreen }}
        >
          <Check size={14} className="text-white" />
        </div>
      )}
      <div className="text-center">
        <div className="text-3xl mb-2">{style.icon}</div>
        <div className="text-sm font-semibold" style={{ color: colors.textDark }}>{milestone.name}</div>
        <div className="text-xs mt-1" style={{ color: colors.textMuted }}>{formattedDate}</div>
      </div>
    </div>
  );
};

// Progress Dots Component
const ProgressDots = ({ currentStep, totalSteps }) => (
  <div className="flex items-center gap-2">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div
        key={i}
        className="w-2.5 h-2.5 rounded-full transition-all"
        style={{
          backgroundColor: i < currentStep ? '#FFFFFF' : 'rgba(255,255,255,0.4)'
        }}
      />
    ))}
  </div>
);

// Main Component
const LetterCreation = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditing = !!editId;
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const quillRef = useRef(null);
  const { families, activeFamily } = useFamily();

  // Flow state: 'recipient', 'milestone', 'writing', 'media', 'review'
  const [currentStep, setCurrentStep] = useState('recipient');
  const [messages, setMessages] = useState([]);

  // Data state
  const [familyMembers, setFamilyMembers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [customDate, setCustomDate] = useState('');
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [letterContent, setLetterContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [familyId, setFamilyId] = useState(null);
  const [selectedSteward, setSelectedSteward] = useState(null);
  const [selectedBackup, setSelectedBackup] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharingScope, setSharingScope] = useState('single_family');
  const [sharingFamilyIds, setSharingFamilyIds] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  // Create milestone modal state
  const [showCreateMilestone, setShowCreateMilestone] = useState(false);
  const [milestoneTemplates, setMilestoneTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newMilestoneDate, setNewMilestoneDate] = useState('');
  const [creatingMilestone, setCreatingMilestone] = useState(false);

  // Get current user's person ID
  const getCurrentUserPersonId = () => {
    try {
      const userData = getUser() || {};
      return userData.person_id || userData.id || null;
    } catch (error) {
      return null;
    }
  };

  // Quill modules
  const modules = useMemo(() => ({
    toolbar: { container: TOOLBAR_OPTIONS },
    clipboard: { matchVisual: false }
  }), []);

  // Scroll to bottom of chat
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentStep]);

  // Fetch family members on mount
  useEffect(() => {
    const fetchFamilyMembers = async () => {
      try {
        // Get family ID from localStorage
        const storedFamilyId = localStorage.getItem('familyId');
        if (!storedFamilyId) {
          // Try to get family ID from families list
          const families = getFamilies();
          if (families && families.length > 0) {
            setFamilyId(families[0].id);
            localStorage.setItem('familyId', families[0].id);
          }
        } else {
          setFamilyId(storedFamilyId);
        }

        const activeFamilyId = storedFamilyId || localStorage.getItem('familyId');
        if (!activeFamilyId) {
          toast.error('No family found. Please join a family first.');
          setLoading(false);
          return;
        }

        // Fetch all family members
        const response = await axiosInstance.get(`/families/${activeFamilyId}/members/`);
        const membersData = response.data.members || response.data.results || response.data || [];

        // Get current user's person ID to exclude self from recipients
        const currentPersonId = getCurrentUserPersonId();

        // Filter out current user and transform data
        const otherMembers = membersData
          .filter(member => member.id !== currentPersonId)
          .map(member => ({
            id: member.id,
            first_name: member.first_name,
            last_name: member.last_name,
            gender: member.gender,
            role: member.role || 'Family member',
            has_account: member.has_account,
            email: member.email,
            photo: member.photo,
            is_minor: member.is_minor,
          }));

        setFamilyMembers(otherMembers);

        // New letters start with the sender's Primary and Backup stewards (changeable per letter)
        if (!editId) {
          try {
            const stewardship = await getStewardship();
            const memberFor = (role) => {
              const designation = stewardship.enabled
                && stewardship.designations.find(d => d.role === role);
              return designation
                && otherMembers.find(m => String(m.id) === String(designation.person_id));
            };
            const primary = memberFor('PRIMARY');
            const backup = memberFor('BACKUP');
            if (primary) setSelectedSteward(prev => prev || primary);
            if (backup) setSelectedBackup(prev => prev || backup);
          } catch (stewardErr) {
            console.error('Could not load default steward:', stewardErr);
          }
        }
      } catch (err) {
        console.error('Error fetching family members:', err);
        toast.error('Failed to load family members');
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyMembers();
  }, [editId]);

  // Load existing capsule when editing
  useEffect(() => {
    const loadCapsuleData = async () => {
      if (!editId) return;

      try {
        const capsule = await getTimeCapsule(editId);
        setLetterContent(capsule.message || '');

        // Set recipient - compare as strings to handle UUID type differences
        if (capsule.receiver) {
          const receiverId = String(capsule.receiver);
          const member = familyMembers.find(m => String(m.id) === receiverId);

          if (member) {
            setSelectedRecipient(member);
          } else {
            // If member not found in list, create from capsule data
            setSelectedRecipient({
              id: capsule.receiver,
              first_name: capsule.receiver_first_name || 'Recipient',
              last_name: capsule.receiver_last_name || '',
              role: 'Family member'
            });
          }
        }

        // Set milestone or date based on deliver_on type
        if (capsule.deliver_on === 'DATE' && capsule.delivery_datetime) {
          // Date-based delivery
          setUseCustomDate(true);
          setCustomDate(capsule.delivery_datetime.split('T')[0]);
        } else if (capsule.deliver_on === 'MILESTONE' && capsule.milestone) {
          // Milestone-based delivery with valid milestone
          setSelectedMilestone({
            id: capsule.milestone,
            name: capsule.milestone_name || 'Milestone'
          });
        } else if (capsule.deliver_on === 'EVENT' && capsule.delivery_event) {
          // Event-based delivery (placeholder milestones like "Wedding Day")
          setSelectedMilestone({
            id: 'event',
            name: capsule.delivery_event
          });
        } else if (capsule.milestone) {
          // Fallback: has milestone ID
          setSelectedMilestone({
            id: capsule.milestone,
            name: capsule.milestone_name || capsule.delivery_event || 'Milestone'
          });
        } else if (capsule.delivery_event && capsule.delivery_event !== 'custom' && capsule.delivery_event !== 'milestone') {
          // Fallback: has delivery event name (not 'custom' or 'milestone' literal)
          setSelectedMilestone({
            id: 'event',
            name: capsule.delivery_event
          });
        } else if (capsule.delivery_datetime) {
          // Fallback: has delivery datetime
          setUseCustomDate(true);
          setCustomDate(capsule.delivery_datetime.split('T')[0]);
        } else {
          console.warn('Letter has no valid delivery configuration, showing as not set');
        }

        // Load media
        if (capsule.media_files_detail?.length > 0) {
          setMediaFiles(capsule.media_files_detail.map(m => ({
            id: m.id,
            preview: m.file_url || m.file,
            file_url: m.file_url || m.file,
            name: m.title || 'Media',
            title: m.title || 'Media',
            media_type: m.media_type,
            file_size: m.file_size || 0,
            isLocal: false // Server file, not local
          })));
        }

        // Load steward if set
        if (capsule.steward) {
          const stewardId = String(capsule.steward);
          const stewardMember = familyMembers.find(m => String(m.person_id) === stewardId || String(m.id) === stewardId);
          if (stewardMember) {
            setSelectedSteward(stewardMember);
          } else {
            // If steward not found in list, create from capsule data
            setSelectedSteward({
              id: capsule.steward,
              person_id: capsule.steward,
              first_name: capsule.steward_first_name || 'Steward',
              last_name: capsule.steward_last_name || '',
            });
          }
        }

        // Load backup steward if set
        if (capsule.backup_steward) {
          const backupId = String(capsule.backup_steward);
          const backupMember = familyMembers.find(m => String(m.person_id) === backupId || String(m.id) === backupId);
          setSelectedBackup(backupMember || {
            id: capsule.backup_steward,
            person_id: capsule.backup_steward,
            first_name: capsule.backup_steward_first_name || 'Backup',
            last_name: capsule.backup_steward_last_name || '',
          });
        }

        // Skip to review step if we have content
        if (capsule.message) {
          setCurrentStep('review');
        }
      } catch (err) {
        console.error('Error loading capsule:', err);
        toast.error('Failed to load letter. Please try again.');
      }
    };

    if (!loading && familyMembers.length > 0 && editId) {
      loadCapsuleData();
    }
  }, [editId, loading, familyMembers]);

  // Default milestone options when none exist — templateId maps to backend MilestoneTemplate
  const defaultMilestones = [
    { id: 'birthday-18', name: '18th Birthday', target_date: null, templateId: 2 },
    { id: 'graduation', name: 'High School Graduation', target_date: null, templateId: 10 },
    { id: 'wedding', name: 'Wedding Day', target_date: null, templateId: 16 },
    { id: 'first-job', name: 'First Job', target_date: null, templateId: 26 },
    { id: 'first-day-school', name: 'First Day of School', target_date: null, templateId: 7 },
  ];

  // Fetch milestones when recipient is selected
  useEffect(() => {
    const fetchMilestones = async () => {
      if (!selectedRecipient?.id) return;

      setLoadingMilestones(true);
      try {
        const data = await getMilestonesForPerson(selectedRecipient.id);
        const milestonesArray = Array.isArray(data) ? data : (data.results || []);
        // Filter for pending milestones
        const pendingMilestones = milestonesArray.filter(m => m.status === 'PENDING' || m.status === 'UPCOMING');

        // Use default milestones if none found for this person
        if (pendingMilestones.length === 0) {
          setMilestones(defaultMilestones);
        } else {
          setMilestones(pendingMilestones);
        }
      } catch (err) {
        console.error('Error fetching milestones:', err);
        // Set default milestone options on error
        setMilestones(defaultMilestones);
      } finally {
        setLoadingMilestones(false);
      }
    };

    fetchMilestones();
  }, [selectedRecipient]);

  // Handle recipient selection
  const handleSelectRecipient = (member) => {
    setSelectedRecipient(member);
    const emoji = member.gender === 'M' ? '👨' : member.gender === 'F' ? '👩' : '👤';
    setMessages(prev => [...prev, {
      type: 'user',
      content: `I want to write to ${member.first_name} ${emoji}`
    }]);

    // Move to milestone step after a brief delay
    setTimeout(() => {
      setCurrentStep('milestone');
    }, 500);
  };

  // Handle milestone selection
  const handleSelectMilestone = (milestone) => {
    setSelectedMilestone(milestone);
    setUseCustomDate(false);
    setMessages(prev => [...prev, {
      type: 'user',
      content: `Deliver on ${milestone.name} ${getMilestoneStyle(milestone.name).icon}`
    }]);

    setTimeout(() => {
      setCurrentStep('writing');
    }, 500);
  };

  // Handle custom date selection
  const handleSelectCustomDate = () => {
    setUseCustomDate(true);
    setSelectedMilestone(null);
  };

  // Fetch milestone templates when opening create modal
  const handleOpenCreateMilestone = async () => {
    setShowCreateMilestone(true);
    try {
      const templates = await getMilestoneTemplatesByCategory();
      setMilestoneTemplates(templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Failed to load milestone templates');
    }
  };

  // Create a new milestone
  const handleCreateMilestone = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a milestone type');
      return;
    }

    try {
      setCreatingMilestone(true);
      const newMilestone = await createMilestoneFromTemplate(
        selectedTemplate.id,
        selectedRecipient.id,
        newMilestoneDate || null,
        null
      );

      // Add to milestones list and select it
      setMilestones(prev => [...prev, newMilestone]);
      handleSelectMilestone(newMilestone);

      // Reset modal state
      setShowCreateMilestone(false);
      setSelectedTemplate(null);
      setNewMilestoneDate('');

      toast.success(`Created "${newMilestone.name}" milestone!`);
    } catch (err) {
      console.error('Error creating milestone:', err);
      toast.error('Failed to create milestone');
    } finally {
      setCreatingMilestone(false);
    }
  };

  const handleConfirmCustomDate = () => {
    if (!customDate) {
      toast.error('Please select a date');
      return;
    }

    setMessages(prev => [...prev, {
      type: 'user',
      content: `Deliver on ${new Date(customDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} 📅`
    }]);

    setTimeout(() => {
      setCurrentStep('writing');
    }, 500);
  };

  // Handle continue to media
  const handleContinueToMedia = () => {
    if (!letterContent.trim()) {
      toast.error('Please write some content first');
      return;
    }

    // Extract preview text from HTML
    const div = document.createElement('div');
    div.innerHTML = letterContent;
    const textPreview = (div.textContent || '').substring(0, 50);

    setMessages(prev => [...prev, {
      type: 'user',
      content: `"${textPreview}${textPreview.length >= 50 ? '...' : ''}" ✍️`
    }]);

    setTimeout(() => {
      setCurrentStep('media');
    }, 500);
  };

  // Handle continue to review
  const handleContinueToReview = () => {
    const mediaCount = mediaFiles.length;
    if (mediaCount > 0) {
      setMessages(prev => [...prev, {
        type: 'user',
        content: `Added ${mediaCount} attachment${mediaCount > 1 ? 's' : ''} 📎`
      }]);
    }

    setTimeout(() => {
      setCurrentStep('review');
    }, 300);
  };

  // Check if a string is a valid UUID
  const isValidUUID = (str) => {
    if (!str || typeof str !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Handle save - uses centralized mediaService for FormData uploads
  const handleSave = async (schedule = false) => {
    setSaving(true);

    try {
      // Separate local files (need uploading) from server files (already uploaded)
      const { local: localFiles, server: serverFiles } = partitionMedia(mediaFiles);

      // Resolve milestone ID — check if it's a real DB milestone (numeric or UUID)
      let milestoneId = selectedMilestone?.id;
      const isPlaceholder = milestoneId && typeof milestoneId === 'string' && !isValidUUID(milestoneId) && isNaN(Number(milestoneId));
      let validMilestoneId = isPlaceholder ? null : (milestoneId || null);

      // If placeholder milestone with a templateId, create a real milestone first
      if (isPlaceholder && selectedMilestone?.templateId && selectedRecipient?.id) {
        try {
          const newMilestone = await createMilestoneFromTemplate(
            selectedMilestone.templateId,
            selectedRecipient.id,
            null,  // target_date
            null   // fallback_date
          );
          if (newMilestone?.id) {
            validMilestoneId = String(newMilestone.id);
          }
        } catch (err) {
          console.warn('Could not auto-create milestone:', err.message);
          // Continue without milestone link — letter still gets saved
        }
      }

      // Determine deliver_on type based on selection
      let deliverOn = 'INSTANT';
      let deliveryEvent = null;
      let deliveryDatetime = null;

      if (useCustomDate && customDate) {
        deliverOn = 'DATE';
        deliveryDatetime = customDate;
      } else if (validMilestoneId) {
        deliverOn = 'MILESTONE';
      } else if (selectedMilestone?.name) {
        // Fallback: no milestone created — use EVENT type
        deliverOn = 'EVENT';
        deliveryEvent = selectedMilestone.name;
      }

      // Build capsule data for backend
      const capsuleData = {
        receiver: selectedRecipient?.id || null,
        message: letterContent,
        deliver_on: deliverOn,
        delivery_event: deliveryEvent,
        delivery_datetime: deliveryDatetime,
        milestone: validMilestoneId,
        status: schedule ? 'SCHEDULED' : 'DRAFT',
        title: !validMilestoneId && selectedMilestone?.name ? `For ${selectedMilestone.name}` : null,
        // Include existing server media IDs (serializer expects 'media_files')
        media_files: serverFiles.map(f => f.id),
        // Stewardship - trusted person to deliver if sender unavailable
        steward: selectedSteward?.person_id || selectedSteward?.id || null,
        backup_steward: selectedBackup?.person_id || selectedBackup?.id || null,
        sharing_scope: sharingScope,
        family_ids: sharingFamilyIds,
      };

      // Use mediaService to handle FormData with files
      let response;
      if (isEditing) {
        response = await updateWithMedia(`/timecapsules/${editId}/`, capsuleData, localFiles);
      } else {
        response = await createWithMedia('/timecapsules/', capsuleData, localFiles);
      }

      const recipientName = selectedRecipient?.first_name || 'recipient';
      if (schedule) {
        toast.success(`Letter scheduled for ${recipientName}! 🎉`);
      } else {
        toast.success('Draft saved! You can finish it later.');
      }

      navigate('/timecapsule');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.message || 'Failed to save letter');
    } finally {
      setSaving(false);
    }
  };

  // Calculate progress
  const getStepNumber = () => {
    const steps = { 'recipient': 1, 'milestone': 2, 'writing': 3, 'media': 4, 'review': 5 };
    return steps[currentStep] || 1;
  };

  // Can schedule?
  const canSchedule = selectedRecipient && (selectedMilestone || (useCustomDate && customDate)) && letterContent.trim();


  if (loading) {
    return (
      <HubLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.backgroundCream }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primaryGreen }} />
            <p style={{ color: colors.textMedium }}>Loading...</p>
          </div>
        </div>
      </HubLayout>
    );
  }

  return (
    <HubLayout>
      <div className="min-h-screen" style={{ backgroundColor: colors.backgroundCream }}>
        {/* Header */}
        <div
          className="-mx-4 -mt-4 sm:-mx-6 lg:-mx-8 px-6 py-4 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${colors.primaryGreen} 0%, ${colors.secondaryGreen} 100%)` }}
        >
          <button
            onClick={() => navigate('/timecapsule')}
            className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">{isEditing ? 'Edit Letter' : 'New Letter'}</span>
          </button>

          <ProgressDots currentStep={getStepNumber()} totalSteps={5} />

          <button
            onClick={() => navigate('/timecapsule')}
            className="text-white/80 hover:text-white text-sm"
          >
            Cancel
          </button>
        </div>

        {/* Chat Container */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div
            className="rounded-2xl shadow-lg overflow-hidden"
            style={{ backgroundColor: colors.chatBg, minHeight: '600px' }}
          >
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>

              {/* Initial greeting */}
              <SystemMessage>
                <p className="font-medium mb-1" style={{ color: colors.textDark }}>
                  Hi! Let's create a letter together. 💚
                </p>
                <p style={{ color: colors.textMedium }}>
                  Who would you like to write to?
                </p>
              </SystemMessage>

              {/* Family member selection - Step 1: scoped to active family */}
              {currentStep === 'recipient' && (
                <RecipientSelector
                  families={activeFamily ? [activeFamily] : families.slice(0, 1)}
                  onSelect={handleSelectRecipient}
                  selectedRecipient={selectedRecipient}
                  currentPersonId={getCurrentUserPersonId()}
                />
              )}

              {/* Show previous messages */}
              {messages.map((msg, idx) => (
                msg.type === 'user' ? (
                  <UserMessage key={idx}>{msg.content}</UserMessage>
                ) : (
                  <SystemMessage key={idx}>
                    <p style={{ color: colors.textDark }}>{msg.content}</p>
                  </SystemMessage>
                )
              ))}

              {/* Milestone selection - Step 2 */}
              {currentStep === 'milestone' && (
                <>
                  <SystemMessage>
                    <p className="font-medium mb-1" style={{ color: colors.textDark }}>
                      Great choice! 💚
                    </p>
                    <p style={{ color: colors.textMedium }}>
                      When should {selectedRecipient?.first_name} receive this letter?
                    </p>
                  </SystemMessage>

                  <div className="mb-6">
                    {loadingMilestones ? (
                      <div className="flex items-center gap-2 p-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: colors.primaryGreen }} />
                        <span style={{ color: colors.textMuted }}>Loading milestones...</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-3 mb-4">
                          {milestones.map((milestone) => (
                            <MilestoneCard
                              key={milestone.id}
                              milestone={milestone}
                              selected={selectedMilestone?.id === milestone.id}
                              onClick={() => handleSelectMilestone(milestone)}
                            />
                          ))}
                          <div
                            className={`rounded-xl p-4 cursor-pointer transition-all hover:shadow-md border flex flex-col items-center justify-center ${
                              useCustomDate ? 'ring-2 ring-offset-2' : ''
                            }`}
                            style={{
                              borderColor: useCustomDate ? colors.primaryGreen : '#E0E0E0',
                              borderStyle: useCustomDate ? 'solid' : 'dashed',
                              backgroundColor: useCustomDate ? '#F0F4E8' : '#FFFFFF',
                              minWidth: '140px',
                              minHeight: '100px'
                            }}
                            onClick={handleSelectCustomDate}
                          >
                            <Calendar size={24} style={{ color: colors.textMedium }} />
                            <span className="text-sm mt-2" style={{ color: colors.textMedium }}>Custom Date</span>
                          </div>

                          {/* Create New Milestone Card */}
                          <div
                            className="rounded-xl p-4 cursor-pointer transition-all hover:shadow-md border border-dashed flex flex-col items-center justify-center"
                            style={{
                              borderColor: colors.secondaryGreen,
                              backgroundColor: '#FFFFFF',
                              minWidth: '140px',
                              minHeight: '100px'
                            }}
                            onClick={handleOpenCreateMilestone}
                          >
                            <Plus size={24} style={{ color: colors.secondaryGreen }} />
                            <span className="text-sm mt-2 text-center" style={{ color: colors.secondaryGreen }}>Create New</span>
                          </div>
                        </div>

                        {/* Custom date picker */}
                        {useCustomDate && (
                          <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ color: colors.textDark }}>
                              Select a specific date:
                            </label>
                            <div className="flex gap-3">
                              <input
                                type="date"
                                value={customDate}
                                onChange={(e) => setCustomDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="flex-1 p-3 border-2 rounded-xl focus:outline-none"
                                style={{ borderColor: colors.primaryGreen }}
                              />
                              <button
                                onClick={handleConfirmCustomDate}
                                className="px-6 py-3 rounded-xl text-white font-medium"
                                style={{ backgroundColor: colors.highlightOrange }}
                              >
                                Continue →
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Tip */}
                    <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F5F0' }}>
                      <p className="text-sm" style={{ color: colors.textMuted }}>
                        💡 Tip: Pick a milestone or tap "Custom Date" to set your own delivery date
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Writing step - Step 3 */}
              {currentStep === 'writing' && (
                <>
                  <SystemMessage>
                    <p className="font-medium mb-1" style={{ color: colors.textDark }}>
                      Perfect! Now for the fun part. ✨
                    </p>
                    <p style={{ color: colors.textMedium }}>
                      Write your letter to {selectedRecipient?.first_name}. Take your time - this is special!
                    </p>
                  </SystemMessage>

                  <div className="mb-6">
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
                      <style>
                        {`
                          .chat-editor .ql-container { min-height: 200px; font-size: 16px; }
                          .chat-editor .ql-editor { min-height: 200px; }
                          .chat-editor .ql-toolbar { border-bottom: 1px solid #E5E5E0; }
                        `}
                      </style>
                      <ReactQuill
                        ref={quillRef}
                        value={letterContent}
                        onChange={setLetterContent}
                        modules={modules}
                        formats={FORMATS}
                        theme="snow"
                        placeholder={`Dear ${selectedRecipient?.first_name},\n\nWrite your heartfelt message here...`}
                        className="chat-editor"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={handleContinueToMedia}
                        disabled={!letterContent.trim()}
                        className="px-6 py-3 rounded-xl text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: colors.highlightOrange }}
                      >
                        Continue → Add Photos/Videos
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Media step - Step 4 */}
              {currentStep === 'media' && (
                <>
                  <SystemMessage>
                    <p className="font-medium mb-1" style={{ color: colors.textDark }}>
                      Would you like to add any photos or videos? 📸
                    </p>
                    <p style={{ color: colors.textMedium }}>
                      Adding memories makes your letter even more special!
                    </p>
                  </SystemMessage>

                  <div className="mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
                      <MediaUploader
                        mediaFiles={mediaFiles}
                        onMediaFilesChange={setMediaFiles}
                        disabled={saving}
                      />
                    </div>

                    <div className="flex justify-between">
                      <button
                        onClick={handleContinueToReview}
                        className="px-6 py-3 rounded-xl font-medium border-2"
                        style={{ borderColor: colors.textMuted, color: colors.textMedium }}
                      >
                        Skip for now
                      </button>
                      <button
                        onClick={handleContinueToReview}
                        className="px-6 py-3 rounded-xl text-white font-medium"
                        style={{ backgroundColor: colors.highlightOrange }}
                      >
                        Continue → Review
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Review step - Step 5 */}
              {currentStep === 'review' && (
                <>
                  <SystemMessage>
                    <p className="font-medium mb-1" style={{ color: colors.textDark }}>
                      Your letter is ready! 🎉
                    </p>
                    <p style={{ color: colors.textMedium }}>
                      Review your letter and schedule it when you're ready.
                    </p>
                  </SystemMessage>

                  <div className="mb-6">
                    {/* Summary Card */}
                    <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
                      <h3 className="font-semibold mb-4" style={{ color: colors.textDark }}>Letter Summary</h3>

                      <div className="space-y-4">
                        {/* Recipient - Editable */}
                        <div
                          className="flex items-center gap-3 p-3 -m-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors group"
                          onClick={() => setCurrentStep('recipient')}
                        >
                          <span className="text-2xl">{selectedRecipient?.gender === 'M' ? '👨' : selectedRecipient?.gender === 'F' ? '👩' : '👤'}</span>
                          <div className="flex-1">
                            <div className="text-sm" style={{ color: colors.textMuted }}>To:</div>
                            <div className="font-medium" style={{ color: colors.textDark }}>
                              {selectedRecipient?.first_name} {selectedRecipient?.last_name}
                            </div>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#F0F4E8', color: colors.primaryGreen }}>
                            Edit
                          </span>
                        </div>

                        {/* Delivery - Editable */}
                        <div
                          className="flex items-center gap-3 p-3 -m-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors group"
                          onClick={() => setCurrentStep('milestone')}
                        >
                          <span className="text-2xl">
                            {useCustomDate ? '📅' : getMilestoneStyle(selectedMilestone?.name).icon}
                          </span>
                          <div className="flex-1">
                            <div className="text-sm" style={{ color: colors.textMuted }}>Deliver on:</div>
                            <div className="font-medium" style={{ color: colors.textDark }}>
                              {useCustomDate
                                ? new Date(customDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                                : selectedMilestone?.name || 'Not set'}
                            </div>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#F0F4E8', color: colors.primaryGreen }}>
                            Edit
                          </span>
                        </div>

                        {/* Letter Content - Editable */}
                        <div
                          className="flex items-center gap-3 p-3 -m-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors group"
                          onClick={() => setCurrentStep('writing')}
                        >
                          <span className="text-2xl">📝</span>
                          <div className="flex-1">
                            <div className="text-sm" style={{ color: colors.textMuted }}>Letter preview:</div>
                            <div
                              className="text-sm italic"
                              style={{ color: colors.textMedium }}
                              dangerouslySetInnerHTML={{
                                __html: letterContent.substring(0, 150) + (letterContent.length > 150 ? '...' : '')
                              }}
                            />
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#F0F4E8', color: colors.primaryGreen }}>
                            Edit
                          </span>
                        </div>

                        {/* Media - Editable */}
                        <div
                          className="p-3 -m-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors group"
                          onClick={() => setCurrentStep('media')}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">📎</span>
                            <div className="flex-1">
                              <div className="text-sm" style={{ color: colors.textMuted }}>Attachments:</div>
                              <div className="font-medium" style={{ color: colors.textDark }}>
                                {mediaFiles.length > 0 ? `${mediaFiles.length} file${mediaFiles.length > 1 ? 's' : ''}` : 'No attachments'}
                              </div>
                            </div>
                            <span className="text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: '#F0F4E8', color: colors.primaryGreen }}>
                              {mediaFiles.length > 0 ? 'Edit' : 'Add'}
                            </span>
                          </div>
                          {/* Thumbnails */}
                          {mediaFiles.length > 0 && (
                            <div className="flex gap-2 mt-3 overflow-x-auto">
                              {mediaFiles.slice(0, 5).map((media) => (
                                <div
                                  key={media.id}
                                  className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                                >
                                  {media.media_type === 'image' ? (
                                    <img
                                      src={media.preview || media.file_url}
                                      alt={media.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : media.media_type === 'video' ? (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                      <span className="text-2xl">🎥</span>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                                      <span className="text-2xl">🎵</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {mediaFiles.length > 5 && (
                                <div className="w-16 h-16 flex-shrink-0 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-medium" style={{ color: colors.textMedium }}>
                                  +{mediaFiles.length - 5}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Steward Selection (optional) */}
                    <StewardSelection
                      familyMembers={familyMembers}
                      selectedSteward={selectedSteward}
                      onStewardChange={setSelectedSteward}
                      recipientId={selectedRecipient?.id}
                      excludeId={selectedBackup?.person_id || selectedBackup?.id}
                      disabled={saving}
                    />

                    {/* Backup Steward (optional): acts only if the steward can't */}
                    <StewardSelection
                      familyMembers={familyMembers}
                      selectedSteward={selectedBackup}
                      onStewardChange={setSelectedBackup}
                      recipientId={selectedRecipient?.id}
                      excludeId={selectedSteward?.person_id || selectedSteward?.id}
                      title="Choose a Backup Steward"
                      roleLabel="Backup Steward"
                      noneLabel="No Backup"
                      compact
                      disabled={saving}
                    />

                    {/* Sharing Scope */}
                    <SharingScope
                      value={sharingScope}
                      onChange={(scope, familyIds) => {
                        setSharingScope(scope);
                        setSharingFamilyIds(familyIds);
                      }}
                      className="mt-4"
                    />

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        className="flex-1 px-6 py-4 rounded-xl font-medium border-2 disabled:opacity-50"
                        style={{ borderColor: colors.textMuted, color: colors.textMedium }}
                      >
                        {saving ? 'Saving...' : '💾 Save Draft'}
                      </button>
                      <button
                        onClick={() => handleSave(true)}
                        disabled={saving || !canSchedule}
                        className="flex-1 px-6 py-4 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ backgroundColor: canSchedule ? colors.primaryGreen : '#999' }}
                      >
                        {saving ? 'Scheduling...' : (
                          <>
                            <Send size={18} />
                            Schedule Letter
                          </>
                        )}
                      </button>
                    </div>

                    {!canSchedule && (
                      <p className="text-center text-sm mt-3" style={{ color: colors.highlightOrange }}>
                        {!selectedRecipient && 'Please select a recipient. '}
                        {!selectedMilestone && !useCustomDate && 'Please set when to deliver. '}
                        {useCustomDate && !customDate && 'Please select a delivery date. '}
                        {!letterContent.trim() && 'Please write your letter. '}
                      </p>
                    )}
                  </div>
                </>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Bottom input area - for custom text input */}
            {(currentStep === 'recipient' || currentStep === 'milestone') && (
              <div className="p-4 border-t" style={{ borderColor: '#E5E5E0' }}>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Or type a custom occasion..."
                    className="flex-1 px-5 py-3 rounded-full border focus:outline-none"
                    style={{ borderColor: '#E5E5E0' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        // Handle custom input
                        if (currentStep === 'milestone') {
                          setSelectedMilestone({ id: 'custom', name: e.target.value.trim() });
                          setMessages(prev => [...prev, {
                            type: 'user',
                            content: `Deliver on: "${e.target.value.trim()}"`
                          }]);
                          e.target.value = '';
                          setTimeout(() => setCurrentStep('writing'), 500);
                        }
                      }
                    }}
                  />
                  <button
                    className="p-3 rounded-full text-white"
                    style={{ backgroundColor: colors.highlightOrange }}
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Floating Summary Button */}
        {(currentStep === 'writing' || currentStep === 'media') && (
          <div className="fixed bottom-6 right-6">
            <button
              onClick={() => setCurrentStep('review')}
              className="px-6 py-3 rounded-full shadow-lg text-white font-medium"
              style={{ backgroundColor: colors.primaryGreen }}
            >
              View Summary
            </button>
          </div>
        )}

        {/* Create Milestone Modal */}
        {showCreateMilestone && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: '#E5E5E0' }}>
                <h3 className="text-lg font-semibold" style={{ color: colors.textDark }}>
                  Create New Milestone for {selectedRecipient?.first_name}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateMilestone(false);
                    setSelectedTemplate(null);
                    setNewMilestoneDate('');
                  }}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <X size={20} style={{ color: colors.textMuted }} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 140px)' }}>
                {/* Template Categories */}
                {Object.keys(milestoneTemplates).length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: colors.primaryGreen }} />
                    <span className="ml-2" style={{ color: colors.textMuted }}>Loading templates...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(milestoneTemplates).map(([category, templates]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium mb-2 uppercase tracking-wide" style={{ color: colors.textMuted }}>
                          {category}
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {templates.map((template) => {
                            const style = getMilestoneStyle(template.name);
                            const isSelected = selectedTemplate?.id === template.id;
                            return (
                              <div
                                key={template.id}
                                className={`rounded-lg p-3 cursor-pointer transition-all border ${
                                  isSelected ? 'ring-2 ring-offset-1' : ''
                                }`}
                                style={{
                                  backgroundColor: isSelected ? style.bg : '#FAFAFA',
                                  borderColor: isSelected ? style.border : '#E5E5E0',
                                  ringColor: colors.primaryGreen
                                }}
                                onClick={() => setSelectedTemplate(template)}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-xl">{template.icon || style.icon}</span>
                                  <span className="text-sm font-medium" style={{ color: colors.textDark }}>
                                    {template.name}
                                  </span>
                                </div>
                                {template.description && (
                                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                                    {template.description}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Date Picker */}
                {selectedTemplate && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E5E5E0' }}>
                    <label className="block text-sm font-medium mb-2" style={{ color: colors.textDark }}>
                      Expected Date (optional)
                    </label>
                    <input
                      type="date"
                      value={newMilestoneDate}
                      onChange={(e) => setNewMilestoneDate(e.target.value)}
                      className="w-full p-3 border-2 rounded-xl focus:outline-none"
                      style={{ borderColor: '#E5E5E0' }}
                      placeholder="Select date"
                    />
                    <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                      You can leave this blank if the date is unknown
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-4 border-t" style={{ borderColor: '#E5E5E0' }}>
                <button
                  onClick={() => {
                    setShowCreateMilestone(false);
                    setSelectedTemplate(null);
                    setNewMilestoneDate('');
                  }}
                  className="flex-1 px-4 py-3 rounded-xl font-medium border-2"
                  style={{ borderColor: colors.textMuted, color: colors.textMedium }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMilestone}
                  disabled={!selectedTemplate || creatingMilestone}
                  className="flex-1 px-4 py-3 rounded-xl text-white font-medium disabled:opacity-50"
                  style={{ backgroundColor: colors.primaryGreen }}
                >
                  {creatingMilestone ? 'Creating...' : 'Create Milestone'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HubLayout>
  );
};

export default LetterCreation;
